import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const HeritageFrame = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-heritage-frame">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Ornate outer border SVG ── */}
      <svg style={{ position: "absolute", inset: 0, zIndex: 2, pointerEvents: "none" }}
        width="420" height="560" viewBox="0 0 420 560">

        {/* outer rect */}
        <rect x="8" y="8" width="404" height="544" rx="2"
          fill="none" stroke="var(--theme-accent)" strokeWidth="1.5" opacity="0.7" />
        {/* inner rect */}
        <rect x="16" y="16" width="388" height="528" rx="1"
          fill="none" stroke="var(--theme-accent)" strokeWidth="0.6" opacity="0.35" />

        {/* ── Corner floral ornaments ── */}
        {[
          [22, 22],
          [398, 22],
          [22, 538],
          [398, 538],
        ].map(([cx, cy], i) => {
          const flipX = i % 2 === 1 ? -1 : 1;
          const flipY = i >= 2 ? -1 : 1;
          return (
            <g key={i} transform={`translate(${cx},${cy}) scale(${flipX},${flipY})`}>
              <path d="M0,0 Q8,-8 16,0" stroke="var(--theme-accent)" strokeWidth="1.2"
                fill="none" opacity="0.7" />
              <path d="M0,0 Q-8,8 0,16" stroke="var(--theme-accent)" strokeWidth="1.2"
                fill="none" opacity="0.7" />
              <circle cx="0" cy="0" r="2.5" fill="var(--theme-accent)" opacity="0.7" />
              <circle cx="16" cy="0" r="1.5" fill="var(--theme-accent)" opacity="0.4" />
              <circle cx="0" cy="16" r="1.5" fill="var(--theme-accent)" opacity="0.4" />
            </g>
          );
        })}

        {/* ── Top ornamental divider ── */}
        <g opacity="0.6">
          <line x1="60" y1="46" x2="170" y2="46" stroke="var(--theme-accent)" strokeWidth="0.8" />
          <polygon points="210,40 215,46 210,52 205,46"
            fill="var(--theme-accent)" opacity="0.9" />
          <line x1="250" y1="46" x2="360" y2="46" stroke="var(--theme-accent)" strokeWidth="0.8" />
          <circle cx="60"  cy="46" r="2" fill="var(--theme-accent)" />
          <circle cx="360" cy="46" r="2" fill="var(--theme-accent)" />
        </g>

        {/* ── Bottom ornamental divider ── */}
        <g opacity="0.6">
          <line x1="60" y1="514" x2="170" y2="514" stroke="var(--theme-accent)" strokeWidth="0.8" />
          <polygon points="210,508 215,514 210,520 205,514"
            fill="var(--theme-accent)" opacity="0.9" />
          <line x1="250" y1="514" x2="360" y2="514" stroke="var(--theme-accent)" strokeWidth="0.8" />
          <circle cx="60"  cy="514" r="2" fill="var(--theme-accent)" />
          <circle cx="360" cy="514" r="2" fill="var(--theme-accent)" />
        </g>

        {/* ── Mid horizontal rule ── */}
        <line x1="30" y1="300" x2="390" y2="300"
          stroke="var(--theme-accent)" strokeWidth="0.6" opacity="0.2" />
      </svg>

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column",
        height: "100%", padding: "26px 30px 20px",
      }}>

        {/* ── Crest / brand header ── */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          {isVisible("logoUrl") && buyer?.logoUrl ? (
            <img src={buyer.logoUrl} alt="Logo"
              style={{ width: 32, height: 32, objectFit: "contain", margin: "0 auto 4px", display: "block" }} />
          ) : (
            /* monogram placeholder */
            <div style={{
              width: 34, height: 34, margin: "0 auto 4px",
              border: "1.5px solid var(--theme-accent)",
              borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{
                fontFamily: "var(--theme-heading-font,'Cinzel','Georgia',serif)",
                fontSize: "0.9rem", fontWeight: 700, color: "var(--theme-accent)",
              }}>
                {(buyer?.organizationName || "J").charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          {isVisible("organizationName") && (
            <div style={{
              fontFamily: "var(--theme-heading-font,'Cinzel','Georgia',serif)",
              fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.3em",
              color: "var(--theme-text)", textTransform: "uppercase",
            }}>
              {(buyer?.organizationName || "MAISON").toUpperCase()}
            </div>
          )}
          <div style={{
            fontSize: "0.47rem", letterSpacing: "0.2em", marginTop: 2,
            color: "var(--theme-textMuted)", textTransform: "uppercase",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            EST. 1892 — {product?.segment?.toUpperCase() || "FINE JEWELLERY"}
          </div>
        </div>

        {/* ── Hero image ── */}
        <div style={{
          flex: "0 0 190px", position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 8,
        }}>
          {/* oval vignette */}
          <div style={{
            position: "absolute",
            width: 190, height: 190, borderRadius: "50%",
            background: "radial-gradient(circle, var(--theme-surface) 0%, transparent 70%)",
            opacity: 0.6, zIndex: 0,
          }} />
          <div style={{
            position: "absolute",
            width: 185, height: 185, borderRadius: "50%",
            border: "1px solid var(--theme-accent)", opacity: 0.25,
          }} />
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "74%", maxHeight: "100%", objectFit: "contain",
              position: "relative", zIndex: 2,
              filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.35))",
            }}
          />
        </div>

        {/* ── Product title ── */}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <h2 style={{
            fontFamily: "var(--theme-heading-font,'Cinzel','Playfair Display',serif)",
            fontSize: "1.15rem", fontWeight: 400,
            color: "var(--theme-text)", letterSpacing: "0.1em",
            margin: "0 0 4px",
          }}>
            {product?.name || "Fine Jewellery"}
          </h2>
          {/* ornament row */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "center", gap: 5,
          }}>
            <div style={{ width: 22, height: 1, background: "var(--theme-accent)", opacity: 0.5 }} />
            <span style={{ color: "var(--theme-accent)", fontSize: "0.6rem", opacity: 0.8 }}>✦</span>
            <div style={{ width: 22, height: 1, background: "var(--theme-accent)", opacity: 0.5 }} />
          </div>
        </div>

        {/* ── Attributes ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 10px",
            border: "1px solid var(--theme-border)",
            borderTop: "1px solid var(--theme-accent)",
            borderBottom: "1px solid var(--theme-accent)",
            padding: "6px 10px", marginBottom: 8,
            background: "var(--theme-surface)",
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.48rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <DiamondShapeSVG shapeName={String(v)} size={22} color="var(--theme-accent)" />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600, color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>{fmtVal(v)}</span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.64rem", fontWeight: 600, color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>{fmtVal(v)}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* ── Price stamp ── */}
        <div style={{
          textAlign: "center", marginBottom: 8,
        }}>
          <div style={{
            display: "inline-block",
            border: "1.5px solid var(--theme-accent)",
            padding: "6px 24px",
            position: "relative",
          }}>
            {/* mini corner ticks */}
            {[
              { top: -3, left: -3 }, { top: -3, right: -3 },
              { bottom: -3, left: -3 }, { bottom: -3, right: -3 },
            ].map((pos, i) => (
              <div key={i} style={{
                position: "absolute", ...pos,
                width: 5, height: 5,
                background: "var(--theme-accent)",
              }} />
            ))}
            <div style={{
              fontFamily: "var(--theme-heading-font,'Cinzel',serif)",
              fontSize: "1.15rem", fontWeight: 700,
              color: "var(--theme-accent)", letterSpacing: "0.05em",
            }}>
              {formatPrice(price)}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid var(--theme-border)", paddingTop: 5,
          fontSize: "0.5rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          letterSpacing: "0.1em", textTransform: "uppercase",
        }}>
          {isVisible("address") && <span>{buyer?.streetAddress || "123 Jewellers Lane"}</span>}
          {isVisible("phone") && <span>{buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>}
        </div>
      </div>
    </div>
  );
});

HeritageFrame.displayName = "HeritageFrame";
export default HeritageFrame;
